import { Button } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  companies,
  contacts,
  offices,
  type Company,
  type Contact,
  type DemergeCompanyResult,
  type Office,
} from "../../../api/addressBook.service";
import LoadingState from "../../../components/LoadingState";
import { PageHeader } from "../../../components/PageHeader";
import { ROUTES } from "../../../routes";
import styles from "../AddressBook.module.css";
import { BUSINESS_TYPE_OPTIONS } from "./businessTypes";

// ============================================================================
// Types
// ============================================================================

type Step = "name" | "offices" | "contacts" | "confirm" | "done";

type WizardState = {
  newCompanyName: string;
  originalName: string;
  globalName: string;
  businessType: string;
  slug: string;
  industry: string;
  website: string;
  notes: string;
  effectiveDate: string;
  selectedOfficeIds: Set<string>;
  selectedContactIds: Set<string>;
};

// ============================================================================
// WizardStepper
// ============================================================================

type StepperStep = { label: string; status: "completed" | "active" | "upcoming" };

function WizardStepper({ steps }: { steps: StepperStep[] }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: "2rem" }}>
      {steps.map((s, i) => (
        <div
          key={s.label}
          style={{ display: "flex", flex: i > 0 ? 1 : undefined, alignItems: "center" }}
        >
          {i > 0 && (
            <div
              style={{
                flex: 1,
                height: 2,
                backgroundColor:
                  s.status === "upcoming" ? "var(--color-border)" : "var(--color-primary, #1a56db)",
              }}
            />
          )}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 14,
                fontWeight: 600,
                backgroundColor:
                  s.status === "completed"
                    ? "var(--color-primary, #1a56db)"
                    : s.status === "active"
                      ? "var(--color-primary, #1a56db)"
                      : "var(--color-background-secondary)",
                color: s.status === "upcoming" ? "var(--color-text-secondary)" : "white",
                border:
                  s.status === "upcoming"
                    ? "2px solid var(--color-border)"
                    : "2px solid var(--color-primary, #1a56db)",
              }}
            >
              {s.status === "completed" ? "✓" : i + 1}
            </div>
            <span
              style={{
                fontSize: 12,
                color:
                  s.status === "active"
                    ? "var(--color-text-primary)"
                    : "var(--color-text-secondary)",
                fontWeight: s.status === "active" ? 600 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {s.label}
            </span>
          </div>
        </div>
      ))}
    </div>
  );
}

const STEP_LABELS = [
  "Name New Company",
  "Select Offices",
  "Select Contact Roles",
  "Review & Confirm",
];
const STEP_KEYS: Step[] = ["name", "offices", "contacts", "confirm"];

function buildStepperSteps(step: Step): StepperStep[] {
  const activeIndex = STEP_KEYS.indexOf(step);
  return STEP_LABELS.map((label, i) => ({
    label,
    status:
      step === "done"
        ? "completed"
        : i < activeIndex
          ? "completed"
          : i === activeIndex
            ? "active"
            : "upcoming",
  }));
}

// ============================================================================
// SectionCard
// ============================================================================

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: "1px solid var(--color-border)",
        borderRadius: 8,
        overflow: "hidden",
        backgroundColor: "var(--color-surface)",
      }}
    >
      <div
        style={{
          padding: "12px 16px",
          backgroundColor: "var(--color-surface)",
          borderBottom: "1px solid var(--color-border)",
        }}
      >
        <div style={{ fontSize: "1rem", fontWeight: 600, color: "var(--color-text-primary)" }}>
          {title}
        </div>
      </div>
      <div style={{ padding: "1rem" }}>{children}</div>
    </div>
  );
}

// ============================================================================
// Step 1 — Name New Company
// ============================================================================

type NameStepProps = {
  sourceCompany: Company;
  wizardState: WizardState;
  onWizardStateChange: (state: WizardState) => void;
  onNext: () => void;
  onCancel: () => void;
};

function NameStep({
  sourceCompany,
  wizardState,
  onWizardStateChange,
  onNext,
  onCancel,
}: NameStepProps) {
  const canNext = wizardState.newCompanyName.trim().length > 0;

  return (
    <div>
      {/* Source company info card */}
      <div
        style={{
          marginBottom: "1.5rem",
          padding: "1rem",
          backgroundColor: "var(--color-background-secondary)",
          borderRadius: 8,
          border: "1px solid var(--color-border)",
        }}
      >
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)", marginBottom: 4 }}>
          Source company (remains active)
        </div>
        <div style={{ fontSize: "1.1rem", fontWeight: 600, color: "var(--color-text-primary)" }}>
          {sourceCompany.name}
        </div>
        {sourceCompany.businessType && (
          <div style={{ fontSize: "0.85rem", color: "var(--color-text-secondary)", marginTop: 4 }}>
            {sourceCompany.businessType}
          </div>
        )}
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)", marginTop: 4 }}>
          Offices will be selectable in the next step.
        </div>
      </div>

      {/* Form fields grid */}
      <div className={styles.formGrid}>
        {/* New Company Name — full width */}
        <div className={`${styles.formField} ${styles.formFieldFull}`}>
          <label className={styles.formLabel} htmlFor="new-company-name">
            New Company Name *
          </label>
          <input
            id="new-company-name"
            type="text"
            className={styles.formInput}
            value={wizardState.newCompanyName}
            onChange={e => onWizardStateChange({ ...wizardState, newCompanyName: e.target.value })}
            placeholder="Enter the name for the new company"
            autoFocus
          />
        </div>

        {/* Original Name — half width */}
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="original-name">
            Original Name
          </label>
          <input
            id="original-name"
            type="text"
            className={styles.formInput}
            value={wizardState.originalName}
            onChange={e => onWizardStateChange({ ...wizardState, originalName: e.target.value })}
            placeholder="Enter original name"
          />
        </div>

        {/* Global Name — half width */}
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="global-name">
            Global Name
          </label>
          <input
            id="global-name"
            type="text"
            className={styles.formInput}
            value={wizardState.globalName}
            onChange={e => onWizardStateChange({ ...wizardState, globalName: e.target.value })}
            placeholder="Enter global name"
          />
        </div>

        {/* Business Type — half width */}
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="business-type">
            Business Type
          </label>
          <select
            id="business-type"
            className={styles.formInput}
            value={wizardState.businessType}
            onChange={e => onWizardStateChange({ ...wizardState, businessType: e.target.value })}
          >
            <option value="">— Select business type —</option>
            {BUSINESS_TYPE_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Industry — half width */}
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="industry">
            Industry
          </label>
          <input
            id="industry"
            type="text"
            className={styles.formInput}
            value={wizardState.industry}
            onChange={e => onWizardStateChange({ ...wizardState, industry: e.target.value })}
            placeholder="Enter industry"
          />
        </div>

        {/* Slug — half width */}
        <div className={styles.formField}>
          <label className={styles.formLabel} htmlFor="slug">
            Slug
          </label>
          <input
            id="slug"
            type="text"
            className={styles.formInput}
            value={wizardState.slug}
            onChange={e => onWizardStateChange({ ...wizardState, slug: e.target.value })}
            placeholder="e.g. acme-law-firm"
          />
        </div>

        {/* Website — full width */}
        <div className={`${styles.formField} ${styles.formFieldFull}`}>
          <label className={styles.formLabel} htmlFor="website">
            Website
          </label>
          <input
            id="website"
            type="text"
            className={styles.formInput}
            value={wizardState.website}
            onChange={e => onWizardStateChange({ ...wizardState, website: e.target.value })}
            placeholder="https://..."
          />
        </div>

        {/* Notes — full width */}
        <div className={`${styles.formField} ${styles.formFieldFull}`}>
          <label className={styles.formLabel} htmlFor="notes">
            Notes
          </label>
          <textarea
            id="notes"
            rows={3}
            className={styles.formInput}
            value={wizardState.notes}
            onChange={e => onWizardStateChange({ ...wizardState, notes: e.target.value })}
            placeholder="Enter notes"
          />
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "2rem",
          paddingTop: "1rem",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <Button variant="outlined" color="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={onNext} disabled={!canNext}>
          Next: Select Offices
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Step 2 — Select Offices
// ============================================================================

type OfficesStepProps = {
  sourceCompany: Company;
  wizardState: WizardState;
  onWizardStateChange: (state: WizardState) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
};

function OfficesStep({
  sourceCompany,
  wizardState,
  onWizardStateChange,
  onNext,
  onBack,
  onCancel,
}: OfficesStepProps) {
  const [allOffices, setAllOffices] = useState<Office[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    offices
      .getAll({ companyId: sourceCompany.id, limit: 500 })
      .then(res => {
        if (!cancelled) setAllOffices(res.data);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load offices");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceCompany.id]);

  function toggleOffice(officeId: string) {
    const next = new Set(wizardState.selectedOfficeIds);
    if (next.has(officeId)) {
      next.delete(officeId);
    } else {
      next.add(officeId);
    }
    onWizardStateChange({ ...wizardState, selectedOfficeIds: next });
  }

  function selectAll() {
    onWizardStateChange({
      ...wizardState,
      selectedOfficeIds: new Set(allOffices.map(o => o.id)),
    });
  }

  function deselectAll() {
    onWizardStateChange({ ...wizardState, selectedOfficeIds: new Set() });
  }

  const selectedCount = wizardState.selectedOfficeIds.size;
  const remainingCount = allOffices.length - selectedCount;

  if (loading) {
    return <LoadingState message="Loading offices..." />;
  }

  if (error) {
    return <p style={{ color: "var(--color-error)" }}>{error}</p>;
  }

  return (
    <div style={{ maxWidth: 720, margin: "0 auto" }}>
      {/* Select all / Deselect all */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1rem",
        }}
      >
        <span style={{ fontSize: "0.9rem", color: "var(--color-text-secondary)" }}>
          {allOffices.length} office{allOffices.length !== 1 ? "s" : ""} in {sourceCompany.name}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="outlined" color="default" onClick={selectAll}>
            Select all
          </Button>
          <Button variant="outlined" color="default" onClick={deselectAll}>
            Deselect all
          </Button>
        </div>
      </div>

      {/* Office list */}
      {allOffices.length === 0 ? (
        <p style={{ color: "var(--color-text-secondary)", fontStyle: "italic" }}>
          No offices found for this company.
        </p>
      ) : (
        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {allOffices.map((office, idx) => {
            const checked = wizardState.selectedOfficeIds.has(office.id);
            const isLast = idx === allOffices.length - 1;
            return (
              <label
                key={office.id}
                style={{
                  display: "flex",
                  width: "100%",
                  boxSizing: "border-box",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  borderBottom: isLast ? "none" : "1px solid var(--color-border)",
                  cursor: "pointer",
                  backgroundColor: checked ? "var(--color-surface)" : "var(--color-background)",
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOffice(office.id)}
                  style={{ width: 16, height: 16, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: "0.9rem",
                      color: "var(--color-text-primary)",
                    }}
                  >
                    {office.name}
                    {office.isHeadOffice && (
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: "0.7rem",
                          fontWeight: 600,
                          padding: "1px 6px",
                          backgroundColor: "var(--color-primary, #1a56db)",
                          color: "white",
                          borderRadius: 10,
                        }}
                      >
                        HQ
                      </span>
                    )}
                  </div>
                  {(office.city || office.countryName) && (
                    <div
                      style={{
                        fontSize: "0.8rem",
                        color: "var(--color-text-secondary)",
                        marginTop: 2,
                      }}
                    >
                      {[office.city, office.countryName].filter(Boolean).join(", ")}
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>
      )}

      {/* Summary bar */}
      <div
        style={{
          marginTop: "1rem",
          padding: "10px 14px",
          backgroundColor: "var(--color-warning-background, #fff8e1)",
          border: "1px solid var(--color-warning-border, #ffc107)",
          borderRadius: 6,
          fontSize: "0.875rem",
          color: "rgb(120, 60, 0)",
        }}
      >
        <strong>{selectedCount}</strong> office{selectedCount !== 1 ? "s" : ""} will move to{" "}
        <strong>{wizardState.newCompanyName || "new company"}</strong>,{" "}
        <strong>{remainingCount}</strong> remain{remainingCount === 1 ? "s" : ""} with{" "}
        <strong>{sourceCompany.name}</strong>.
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          marginTop: "2rem",
          paddingTop: "1rem",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <Button variant="outlined" color="default" onClick={onCancel}>
          Cancel
        </Button>
        <div style={{ display: "flex", gap: 12 }}>
          <Button variant="outlined" color="default" onClick={onBack}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={onNext}>
            Next: Select Contact Roles
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 3 — Select Contact Roles
// ============================================================================

type ContactsStepProps = {
  sourceCompany: Company;
  allOffices: Office[];
  wizardState: WizardState;
  onWizardStateChange: (state: WizardState) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
};

function ContactsStep({
  sourceCompany,
  allOffices,
  wizardState,
  onWizardStateChange,
  onNext,
  onBack,
  onCancel,
}: ContactsStepProps) {
  const [allContacts, setAllContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initialisedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    contacts
      .getAll({ companyId: sourceCompany.id, limit: 500 })
      .then(res => {
        if (cancelled) return;
        setAllContacts(res.data);
        if (!initialisedRef.current) {
          initialisedRef.current = true;
          const autoSelected = new Set(
            res.data
              .filter(c => c.officeId && wizardState.selectedOfficeIds.has(c.officeId))
              .map(c => c.id),
          );
          onWizardStateChange({ ...wizardState, selectedContactIds: autoSelected });
        }
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load contacts");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceCompany.id]);

  function toggleContact(contactId: string) {
    const next = new Set(wizardState.selectedContactIds);
    if (next.has(contactId)) {
      next.delete(contactId);
    } else {
      next.add(contactId);
    }
    onWizardStateChange({ ...wizardState, selectedContactIds: next });
  }

  const selectedCount = wizardState.selectedContactIds.size;

  if (loading) {
    return <LoadingState message="Loading contacts..." />;
  }

  if (error) {
    return <p style={{ color: "var(--color-error)" }}>{error}</p>;
  }

  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }}>
      {/* Table */}
      {allContacts.length === 0 ? (
        <p style={{ color: "var(--color-text-secondary)", fontStyle: "italic" }}>
          No contacts found for this company.
        </p>
      ) : (
        <div
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--font-size-sm)" }}
          >
            <thead>
              <tr>
                {["", "Name", "Job Title", "Office", "Country"].map(h => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "8px 12px",
                      backgroundColor: "var(--color-background-secondary)",
                      borderBottom: "1px solid var(--color-border)",
                      color: "var(--color-text-secondary)",
                      fontWeight: 600,
                      fontSize: "0.8rem",
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allContacts.map((contact, idx) => {
                const checked = wizardState.selectedContactIds.has(contact.id);
                const isLast = idx === allContacts.length - 1;
                return (
                  <tr
                    key={contact.id}
                    style={{
                      backgroundColor: checked ? "var(--color-surface)" : "var(--color-background)",
                      borderBottom: isLast ? "none" : "1px solid var(--color-border)",
                      cursor: "pointer",
                    }}
                    onClick={() => toggleContact(contact.id)}
                  >
                    <td style={{ padding: "10px 12px", width: 40 }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleContact(contact.id)}
                        onClick={e => e.stopPropagation()}
                        style={{ width: 16, height: 16 }}
                      />
                    </td>
                    <td
                      style={{
                        padding: "10px 12px",
                        color: "var(--color-text-primary)",
                        fontWeight: 500,
                      }}
                    >
                      {contact.name}
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--color-text-secondary)" }}>
                      {contact.jobTitle ?? "—"}
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--color-text-secondary)" }}>
                      {contact.officeName ?? "—"}
                    </td>
                    <td style={{ padding: "10px 12px", color: "var(--color-text-secondary)" }}>
                      {contact.countryName ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div
        style={{
          marginTop: "1rem",
          padding: "10px 14px",
          backgroundColor: "var(--color-warning-background, #fff8e1)",
          border: "1px solid var(--color-warning-border, #ffc107)",
          borderRadius: 6,
          fontSize: "0.875rem",
          color: "rgb(120, 60, 0)",
        }}
      >
        <strong>{selectedCount}</strong> contact{selectedCount !== 1 ? "s" : ""} will move to{" "}
        <strong>{wizardState.newCompanyName || "new company"}</strong>.
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          marginTop: "2rem",
          paddingTop: "1rem",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <Button variant="outlined" color="default" onClick={onCancel}>
          Cancel
        </Button>
        <div style={{ display: "flex", gap: 12 }}>
          <Button variant="outlined" color="default" onClick={onBack}>
            Back
          </Button>
          <Button variant="contained" color="primary" onClick={onNext}>
            Next: Review & Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step 4 — Review & Confirm
// ============================================================================

type ConfirmStepProps = {
  sourceCompany: Company;
  wizardState: WizardState;
  allOffices: Office[];
  allContacts: Contact[];
  onBack: () => void;
  onCancel: () => void;
  onDone: (result: DemergeCompanyResult) => void;
};

function ConfirmStep({
  sourceCompany,
  wizardState,
  allOffices,
  allContacts,
  onBack,
  onCancel,
  onDone,
}: ConfirmStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const movingOffices = allOffices.filter(o => wizardState.selectedOfficeIds.has(o.id));
  const stayingOffices = allOffices.filter(o => !wizardState.selectedOfficeIds.has(o.id));
  const movingContacts = allContacts.filter(c => wizardState.selectedContactIds.has(c.id));
  const stayingContacts = allContacts.filter(c => !wizardState.selectedContactIds.has(c.id));

  async function handleConfirm() {
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await companies.demerge(sourceCompany.id, {
        newCompanies: [
          {
            name: wizardState.newCompanyName,
            originalName: wizardState.originalName || null,
            globalName: wizardState.globalName || null,
            businessType: wizardState.businessType || null,
            slug: wizardState.slug || null,
            industry: wizardState.industry || null,
            website: wizardState.website || null,
            notes: wizardState.notes || null,
            officeIds: Array.from(wizardState.selectedOfficeIds),
            contactIds: Array.from(wizardState.selectedContactIds),
          },
        ],
        effectiveDate: `${wizardState.effectiveDate}T00:00:00.000Z`,
      });
      onDone(res.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Demerge failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }

  const fieldRowStyle: React.CSSProperties = {
    display: "flex",
    gap: 8,
    marginBottom: 6,
    fontSize: "0.9rem",
  };
  const labelStyle: React.CSSProperties = {
    color: "var(--color-text-secondary)",
    minWidth: 120,
    flexShrink: 0,
  };
  const valueStyle: React.CSSProperties = { color: "var(--color-text-primary)", fontWeight: 500 };

  return (
    <div
      style={{
        maxWidth: 720,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "1.25rem",
      }}
    >
      {/* New company summary */}
      <SectionCard title="New Company">
        <div style={fieldRowStyle}>
          <span style={labelStyle}>Name</span>
          <span style={valueStyle}>{wizardState.newCompanyName}</span>
        </div>
        {wizardState.businessType && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Business Type</span>
            <span style={valueStyle}>{wizardState.businessType}</span>
          </div>
        )}
        {wizardState.industry && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Industry</span>
            <span style={valueStyle}>{wizardState.industry}</span>
          </div>
        )}
        {wizardState.originalName && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Original Name</span>
            <span style={valueStyle}>{wizardState.originalName}</span>
          </div>
        )}
        {wizardState.globalName && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Global Name</span>
            <span style={valueStyle}>{wizardState.globalName}</span>
          </div>
        )}
        {wizardState.slug && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Slug</span>
            <span style={valueStyle}>{wizardState.slug}</span>
          </div>
        )}
        {wizardState.website && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Website</span>
            <span style={valueStyle}>{wizardState.website}</span>
          </div>
        )}
        {wizardState.notes && (
          <div style={fieldRowStyle}>
            <span style={labelStyle}>Notes</span>
            <span style={valueStyle}>{wizardState.notes}</span>
          </div>
        )}
      </SectionCard>

      {/* Offices moving */}
      <SectionCard
        title={`Offices moving to "${wizardState.newCompanyName}" (${movingOffices.length})`}
      >
        {movingOffices.length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", fontStyle: "italic", margin: 0 }}>
            No offices selected
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {movingOffices.map(o => (
              <li
                key={o.id}
                style={{ fontSize: "0.9rem", color: "var(--color-text-primary)", marginBottom: 4 }}
              >
                {o.name}
                {(o.city || o.countryName) && (
                  <span style={{ color: "var(--color-text-secondary)", marginLeft: 6 }}>
                    — {[o.city, o.countryName].filter(Boolean).join(", ")}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* Contacts moving */}
      <SectionCard
        title={`Contacts moving to "${wizardState.newCompanyName}" (${movingContacts.length})`}
      >
        {movingContacts.length === 0 ? (
          <p style={{ color: "var(--color-text-secondary)", fontStyle: "italic", margin: 0 }}>
            No contacts selected
          </p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            {movingContacts.map(c => (
              <li
                key={c.id}
                style={{ fontSize: "0.9rem", color: "var(--color-text-primary)", marginBottom: 4 }}
              >
                {c.name}
                {c.jobTitle && (
                  <span style={{ color: "var(--color-text-secondary)", marginLeft: 6 }}>
                    — {c.jobTitle}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </SectionCard>

      {/* Staying with source */}
      <SectionCard title={`Remaining with ${sourceCompany.name}`}>
        <div style={fieldRowStyle}>
          <span style={labelStyle}>Offices</span>
          <span style={valueStyle}>{stayingOffices.length}</span>
        </div>
        <div style={fieldRowStyle}>
          <span style={labelStyle}>Contacts</span>
          <span style={valueStyle}>{stayingContacts.length}</span>
        </div>
      </SectionCard>

      {/* Warning */}
      <div
        style={{
          background: "var(--color-warning-background, #fff8e1)",
          border: "1px solid var(--color-warning-border, #ffc107)",
          borderRadius: 6,
          padding: "12px 16px",
          fontSize: "0.875rem",
          color: "rgb(120, 60, 0)",
        }}
      >
        <strong>Warning:</strong> This action creates a <code>split_from</code> relationship record.
        The source company <strong>{sourceCompany.name}</strong> remains active.
      </div>

      {/* Error */}
      {error && (
        <p style={{ color: "var(--color-error)", fontSize: "0.875rem", margin: 0 }}>{error}</p>
      )}

      {/* Footer */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          paddingTop: "1rem",
          borderTop: "1px solid var(--color-border)",
        }}
      >
        <Button variant="outlined" color="default" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <div style={{ display: "flex", gap: 12 }}>
          <Button variant="outlined" color="default" onClick={onBack} disabled={isSubmitting}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Demerging..." : "Confirm Demerge"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Step "done" — Success
// ============================================================================

type DoneStepProps = {
  sourceCompany: Company;
  newCompanyName: string;
  demergeResult: DemergeCompanyResult;
};

function DoneStep({ sourceCompany, newCompanyName, demergeResult }: DoneStepProps) {
  const navigate = useNavigate();

  return (
    <div style={{ textAlign: "center", padding: "3rem 1rem" }}>
      <p
        style={{
          fontSize: "1.5rem",
          fontWeight: 700,
          color: "var(--color-text-primary)",
          marginBottom: "0.75rem",
        }}
      >
        Demerge complete!
      </p>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "0.5rem" }}>
        <strong>{newCompanyName}</strong> has been created as a split-off from{" "}
        <strong>{sourceCompany.name}</strong>.
      </p>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "2rem" }}>
        {demergeResult.officesTransferred} office
        {demergeResult.officesTransferred !== 1 ? "s" : ""} transferred.
      </p>
      <div style={{ display: "flex", justifyContent: "center", gap: "12px", flexWrap: "wrap" }}>
        <Button
          variant="outlined"
          color="default"
          onClick={() => navigate(ROUTES.addressBook.companyDetail(sourceCompany.id))}
        >
          View {sourceCompany.name}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(ROUTES.addressBook.companies)}
        >
          Back to Companies
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// DemergeWizardPage — main page component
// ============================================================================

export default function DemergeWizardPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [step, setStep] = useState<Step>("name");
  const [sourceCompany, setSourceCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [wizardState, setWizardState] = useState<WizardState>({
    newCompanyName: "",
    originalName: "",
    globalName: "",
    businessType: "",
    slug: "",
    industry: "",
    website: "",
    notes: "",
    effectiveDate: new Date().toISOString().split("T")[0],
    selectedOfficeIds: new Set(),
    selectedContactIds: new Set(),
  });

  // Cached lists so ConfirmStep can display names without re-fetching
  const [cachedOffices, setCachedOffices] = useState<Office[]>([]);
  const [cachedContacts, setCachedContacts] = useState<Contact[]>([]);

  const [demergeResult, setDemergeResult] = useState<DemergeCompanyResult | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    companies
      .getById(id)
      .then(res => {
        setSourceCompany(res.data);
        setWizardState(prev => ({
          ...prev,
          industry: res.data.industry ?? "",
        }));
      })
      .catch(err => setLoadError(err instanceof Error ? err.message : "Failed to load company"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Demerge Company" subtitle="Loading..." />
        <LoadingState message="Loading company..." />
      </div>
    );
  }

  if (loadError || !sourceCompany) {
    return (
      <div className={styles.detailWrapper}>
        <PageHeader title="Demerge Company" subtitle="An error occurred" />
        <p style={{ color: "var(--color-error)" }}>{loadError ?? "Company not found"}</p>
        <div style={{ marginTop: "1rem" }}>
          <Button
            variant="outlined"
            color="default"
            onClick={() => navigate(ROUTES.addressBook.companies)}
          >
            Back to Companies
          </Button>
        </div>
      </div>
    );
  }

  const stepTitles: Record<Step, { title: string; subtitle: string }> = {
    name: {
      title: "Demerge Company",
      subtitle: "Create a new split-off company",
    },
    offices: {
      title: "Select Offices",
      subtitle: `Choose which offices will move to "${wizardState.newCompanyName}"`,
    },
    contacts: {
      title: "Select Contact Roles",
      subtitle: `Choose which contacts will move to "${wizardState.newCompanyName}"`,
    },
    confirm: {
      title: "Review & Confirm",
      subtitle: `Split off "${wizardState.newCompanyName}" from ${sourceCompany.name}`,
    },
    done: {
      title: "Demerge Complete",
      subtitle: "The company split has been completed successfully.",
    },
  };

  const { title, subtitle } = stepTitles[step];
  const cancelDestination = () => navigate(ROUTES.addressBook.companyDetail(sourceCompany.id));

  return (
    <div className={styles.detailWrapper}>
      <PageHeader title={title} subtitle={subtitle} />
      <WizardStepper steps={buildStepperSteps(step)} />

      {step === "name" && (
        <NameStep
          sourceCompany={sourceCompany}
          wizardState={wizardState}
          onWizardStateChange={setWizardState}
          onNext={() => setStep("offices")}
          onCancel={cancelDestination}
        />
      )}

      {step === "offices" && (
        <OfficesStep
          sourceCompany={sourceCompany}
          wizardState={wizardState}
          onWizardStateChange={next => {
            setWizardState(next);
            // Keep cached offices updated for ConfirmStep
            offices
              .getAll({ companyId: sourceCompany.id, limit: 500 })
              .then(res => setCachedOffices(res.data))
              .catch(err => {
                console.error("Failed to refresh offices cache after wizard state change", err);
              });
          }}
          onNext={() => setStep("contacts")}
          onBack={() => setStep("name")}
          onCancel={cancelDestination}
        />
      )}

      {step === "contacts" && (
        <ContactsStep
          sourceCompany={sourceCompany}
          allOffices={cachedOffices}
          wizardState={wizardState}
          onWizardStateChange={next => {
            setWizardState(next);
            // Keep cached contacts updated for ConfirmStep
            Promise.allSettled(
              cachedOffices.map(o => contacts.getAll({ officeId: o.id, limit: 500 })),
            )
              .then(results => {
                const seen = new Set<string>();
                const allContactsList: Contact[] = [];
                for (const r of results) {
                  if (r.status === "fulfilled") {
                    for (const c of r.value.data) {
                      if (!seen.has(c.id)) {
                        seen.add(c.id);
                        allContactsList.push(c);
                      }
                    }
                  }
                }
                setCachedContacts(allContactsList);
              })
              .catch(err => {
                console.error("Failed to refresh contacts cache after wizard state change", err);
              });
          }}
          onNext={() => setStep("confirm")}
          onBack={() => setStep("offices")}
          onCancel={cancelDestination}
        />
      )}

      {step === "confirm" && (
        <ConfirmStep
          sourceCompany={sourceCompany}
          wizardState={wizardState}
          allOffices={cachedOffices}
          allContacts={cachedContacts}
          onBack={() => setStep("contacts")}
          onCancel={cancelDestination}
          onDone={result => {
            setDemergeResult(result);
            setStep("done");
          }}
        />
      )}

      {step === "done" && demergeResult && (
        <DoneStep
          sourceCompany={sourceCompany}
          newCompanyName={wizardState.newCompanyName}
          demergeResult={demergeResult}
        />
      )}
    </div>
  );
}
